/**
 * =============================================================================
 * Página: /afiliado/links → "Minhas Campanhas"
 * Versão: 4.0.0 (2026-05-01) — Galeria premium com KPIs ampliados e capa
 *
 * Descrição:
 *   Lista as campanhas ativas no formato de galeria (estilo Materiais),
 *   com capa visual (imagem/vídeo), KPIs ampliados (cliques, indicações,
 *   conversões, total ganho) e botão de Regras quando a campanha tem
 *   modelo de contrato vinculado.
 * =============================================================================
 */
import { Megaphone, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAffiliateMyCampaigns,
  type MyCampaignLink,
} from '@/hooks/useAffiliateMyCampaigns';
import { CampaignCard } from '@/components/affiliate/CampaignCard';
import { AffiliatePageHeader } from '@/components/affiliate/shared/AffiliatePageHeader';
import { AffiliateEmptyState } from '@/components/affiliate/shared/AffiliateEmptyState';

export default function AffiliateLinks() {
  const { data: campaigns, isLoading, isError, refetch } = useAffiliateMyCampaigns();

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-6 space-y-6 aff-animate-in">
      <AffiliatePageHeader
        icon={<Megaphone className="h-5 w-5" />}
        title="Minhas Campanhas"
        subtitle="Copie o link pessoal e comece a indicar agora."
      />

      {isLoading && <CampaignsSkeleton />}

      {isError && !isLoading && (
        <div className="aff-card flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar suas campanhas.
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && (campaigns?.length ?? 0) === 0 && (
        <AffiliateEmptyState
          icon={<Sparkles className="h-7 w-7" />}
          title="Nenhuma campanha disponível"
          description="Quando o administrador liberar uma campanha de indicação, ela aparecerá aqui com o seu link pessoal pronto para divulgar."
        />
      )}

      {!isLoading && !isError && (campaigns?.length ?? 0) > 0 && (
        <CampaignsGrid campaigns={campaigns!} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Subcomponentes
// -----------------------------------------------------------------------------

function CampaignsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-[420px] w-full rounded-2xl" />
      ))}
    </div>
  );
}

function CampaignsGrid({ campaigns }: { campaigns: MyCampaignLink[] }) {
  // Agrupa por programa para manter contexto quando o afiliado participa
  // de múltiplos programas.
  const byProgram = campaigns.reduce<
    Record<string, { name: string; items: MyCampaignLink[] }>
  >((acc, c) => {
    if (!acc[c.program_id]) {
      acc[c.program_id] = { name: c.program_name, items: [] };
    }
    acc[c.program_id].items.push(c);
    return acc;
  }, {});

  const groups = Object.entries(byProgram);
  const showProgramHeader = groups.length > 1;

  return (
    <div className="space-y-8">
      {groups.map(([programId, group]) => (
        <section
          key={programId}
          className="space-y-3"
          aria-label={`Campanhas — ${group.name}`}
        >
          {showProgramHeader && (
            <div className="flex items-center gap-2 px-1">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--aff-muted-foreground))]">
                {group.name}
              </h2>
              <span className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                · {group.items.length} campanha{group.items.length === 1 ? '' : 's'}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.items.map((c) => (
              <CampaignCard key={c.campaign_id} campaign={c} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
